import { useArchiveSkill } from "@app/lib/swr/skill_configurations";
import type { SkillWithoutInstructionsAndToolsWithRelationsType } from "@app/types/assistant/skill_configuration";
import { removeNulls } from "@app/types/shared/utils/general";
import { pluralize } from "@app/types/shared/utils/string_utils";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Dialog,
  DialogContainer,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@dust-tt/sparkle";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";

interface DeleteSkillDialogProps {
  skill: SkillWithoutInstructionsAndToolsWithRelationsType;
  isOpen: boolean;
  onClose: () => void;
  owner: LightWorkspaceType;
}

export function ArchiveSkillDialog({
  skill,
  isOpen,
  onClose,
  owner,
}: DeleteSkillDialogProps) {
  const { t } = useTranslation();
  const [isArchiving, setIsArchiving] = useState(false);
  const doArchive = useArchiveSkill({ owner, skill: skill });
  const agentsUsageCount = skill.relations.usage.agents.length;
  const skillsUsageCount = skill.relations.usage.skills.length;
  const usageLabels = removeNulls([
    agentsUsageCount > 0
      ? t("builder.skillArchiveUsageAgents", "{{count}} agent{{plural}}", {
          count: agentsUsageCount,
          plural: pluralize(agentsUsageCount),
        })
      : null,
    skillsUsageCount > 0
      ? t("builder.skillArchiveUsageSkills", "{{count}} skill{{plural}}", {
          count: skillsUsageCount,
          plural: pluralize(skillsUsageCount),
        })
      : null,
  ]);
  const usageText =
    usageLabels.length === 2
      ? t("builder.skillArchiveUsageJoined", "{{first}} and {{second}}", {
          first: usageLabels[0],
          second: usageLabels[1],
        })
      : usageLabels[0];

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent size="md" isAlertDialog>
        <DialogHeader hideButton>
          <DialogTitle>
            {t("builder.skillArchivingTitle", "Archiving the skill")}
          </DialogTitle>
          <DialogDescription>
            <div>
              {usageLabels.length === 0 ? (
                <Trans
                  i18nKey="builder.skillArchiveConfirmBodyEveryone"
                  defaults="This will archive the skill <0>{{name}}</0> for everyone."
                  values={{ name: skill?.name }}
                  components={[<span key="0" className="font-bold" />]}
                />
              ) : (
                <Trans
                  i18nKey="builder.skillArchiveConfirmBodyUsedBy"
                  defaults="This will archive the skill <0>{{name}}</0> used by {{usage}}."
                  values={{
                    name: skill?.name,
                    usage: usageText,
                  }}
                  components={[<span key="0" className="font-bold" />]}
                />
              )}
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogContainer>
          <div className="text-sm font-medium">
            {t(
              "builder.skillActionAreYouSure",
              "Are you sure you want to proceed?"
            )}
          </div>
        </DialogContainer>
        <DialogFooter
          leftButtonProps={{
            label: t("builder.skillCancelButton", "Cancel"),
            disabled: isArchiving,
            variant: "outline",
          }}
          rightButtonProps={{
            label: t("builder.skillArchiveForEveryone", "Archive for everyone"),
            disabled: isArchiving,
            isLoading: isArchiving,
            variant: "warning",
            onClick: async (e: React.MouseEvent) => {
              e.preventDefault();
              setIsArchiving(true);
              await doArchive();
              setIsArchiving(false);
              onClose();
            },
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
